import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Select,
  Notification,
} from '@arco-design/web-react'
import { FormInstance } from '@arco-design/web-react/es/Form'
import {
  IconLanguage,
  IconLock,
  IconUser,
  IconArrowRight,
} from '@arco-design/web-react/icon'
import axios from 'axios'
import useStorage from '@/hooks/useStorage'
import { useAxios } from '@/hooks/useAxios'
import useLocale from '@/hooks/useLocale'
import locale from './locale'
import styles from './style/index.module.less'
import {
  inviteRegisterUrl,
  loginUrl,
  registerServerUserUrl,
} from '@/apis/account'
import {
  EMAIL_REGEX,
  getLoginIdentifierValidationError,
  PHONE_REGEX,
} from './validators'
import IconButton from '@/components/NavBar/IconButton'
import { GlobalContext } from '@/context'
import { Grid } from '@arco-design/web-react'
import { bcBaseUrl, getSetting } from '@/utils/settings'
import { useHistory, useLocation } from 'react-router-dom'
const Row = Grid.Row
const Col = Grid.Col
const SYSTEM_SITE_URL = bcBaseUrl()
const KEY_TOKEN_LOGIN = getSetting('KEY_TOKEN_LOGIN')
const KEY_USER_INFO = getSetting('KEY_USER_INFO')

function resolveApiErrorMessage(error: any, fallback: string) {
  const content =
    error?.response?.data?.error?.message ||
    error?.response?.data?.result?.message ||
    error?.response?.data?.message ||
    error?.error?.message ||
    error?.result?.message ||
    error?.message ||
    ''

  if (!content || content === 'Network Error') {
    return fallback
  }
  return content
}

function showErrorNotice(title: string, content: string, duration = 1500) {
  Notification.error({
    title,
    content,
    showIcon: true,
    position: 'bottomRight',
    duration,
  })
}

export default function LoginForm() {
  const formRef = useRef<FormInstance | null>(null)
  const hasInitAxiosRef = useRef(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const location = useLocation()
  const [loginParams, setLoginParams, removeLoginParams] =
    useStorage('loginParams')
  const [, setUserInfo] = useStorage(KEY_USER_INFO)
  const [, setToken] = useStorage(KEY_TOKEN_LOGIN)
  const { setLang, lang } = useContext(GlobalContext)
  const initAxios = useAxios()
  const t = useLocale(locale)
  const rememberPassword = !!loginParams
  const isRegisterMode = useMemo(() => {
    return /^\/register\/?$/.test(location.pathname)
  }, [location.pathname])
  const registerType = useMemo(() => {
    return new URLSearchParams(location.search).get('type') || '1'
  }, [location.search])
  const inviteCodeFromUrl = useMemo(() => {
    return new URLSearchParams(location.search).get('inviteCode') || ''
  }, [location.search])
  const companyFromUrl = useMemo(() => {
    return new URLSearchParams(location.search).get('company') || ''
  }, [location.search])
  const isMainAccountRegister = isRegisterMode && registerType === '1'
  const isSubAccountRegister = isRegisterMode && registerType === '2'
  const canSubmitRegister =
    !isRegisterMode ||
    isMainAccountRegister ||
    (isSubAccountRegister && !!inviteCodeFromUrl)

  useEffect(() => {
    if (hasInitAxiosRef.current) return
    initAxios(false)
    hasInitAxiosRef.current = true
  }, [initAxios])

  useEffect(() => {
    if (isSubAccountRegister) {
      formRef.current?.setFieldValue('inviteCode', inviteCodeFromUrl)
      if (companyFromUrl) {
        formRef.current?.setFieldValue('name', companyFromUrl)
      }
      return
    }
    if (isRegisterMode && inviteCodeFromUrl && isMainAccountRegister) {
      formRef.current?.setFieldValue('inviteCode', inviteCodeFromUrl)
    }
    if (isRegisterMode && companyFromUrl) {
      formRef.current?.setFieldValue('name', companyFromUrl)
    }
  }, [
    companyFromUrl,
    inviteCodeFromUrl,
    isMainAccountRegister,
    isRegisterMode,
    isSubAccountRegister,
  ])

  useEffect(() => {
    const touchedFields = formRef.current?.getTouchedFields?.() || []
    if (!touchedFields.length) return
    formRef.current?.validate?.(touchedFields as any).catch(() => {
      console.log('validate error', touchedFields)
    })
  }, [lang])

  function afterLoginSuccess(params, jumpUrl?: string) {
    // 记住密码
    if (rememberPassword) {
      setLoginParams(JSON.stringify(params))
    } else {
      removeLoginParams()
    }
    // 已登录后，若当前在登录/注册路由，回到首页；否则保留原目标路由
    const isAuthRoute = /^\/(login|register)\/?$/.test(location.pathname)
    const targetPath = isAuthRoute
      ? '/'
      : `${location.pathname}${location.search}${location.hash}`
    window.location.href = jumpUrl || targetPath
  }

  function login(params) {
    const loginIdentifier = `${params?.phoneNumber || ''}`.trim()
    const loginIdentifierError =
      getLoginIdentifierValidationError(loginIdentifier)

    if (loginIdentifierError) {
      const content =
        loginIdentifierError === 'empty'
          ? t['login.form.loginAccount.errMsg']
          : t['login.form.loginAccount.invalid']
      setErrorMessage(content)
      showErrorNotice(t['login.msg.title'], content)
      return
    }

    setErrorMessage('')
    setLoading(true)
    axios
      .post(loginUrl(), {
        userName: loginIdentifier,
        password: params.password,
      })
      .then((response) => {
        if (response.status === 200) {
          if (response.data?.result?.succeeded === true) {
            const loginToken = response.data?.ucToken || response.data?.token || ''
            setUserInfo(JSON.stringify(response.data))
            setToken(loginToken)
            afterLoginSuccess(params, response.data?.jumpUrl || '')
          } else {
            let content = t['login.msg.userOrPwdError']
            if (response.data?.result?.isNotAllowed) {
              setErrorMessage(t['login.msg.isNotAllowed'])
              content = t['login.msg.isNotAllowed']
            } else {
              setErrorMessage(t['login.msg.userOrPwdError'])
              content = t['login.msg.userOrPwdError']
            }
            showErrorNotice(t['login.msg.title'], content)
          }
        } else {
          const content = t['login.msg.userOrPwdError']
          setErrorMessage(content)
          showErrorNotice(t['login.msg.title'], content)
        }
      })
      .catch((error) => {
        const content = resolveApiErrorMessage(error, t['login.form.login.errMsg'])
        setErrorMessage(content)
        showErrorNotice(t['login.msg.title'], content)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  async function register(params) {
    setErrorMessage('')
    setLoading(true)
    try {
      let response
      if (isMainAccountRegister) {
        response = await axios.post(registerServerUserUrl(), {
          name: params.name || '',
          email: params.email,
          phoneNumber: params.phoneNumber,
          invitationCode: params.inviteCode || '',
          // invitaCode:params.inviteCode || '',
        })
      } else if (isSubAccountRegister) {
        response = await axios.post(inviteRegisterUrl(), {
          email: params.email,
          phoneNumber: params.phoneNumber,
          inviteCode: inviteCodeFromUrl,
          // invitaCode:inviteCodeFromUrl || '',
          invitationCode: inviteCodeFromUrl,
        })
      } else {
        return
      }

      if (response.status === 200 || response.status === 204) {
        const registerJumpUrl = response.data?.jumpUrl || SYSTEM_SITE_URL
        Notification.success({
          title: t['login.msg.registerTitle'],
          content: t['login.msg.registerSuccess'],
          showIcon: true,
          position: 'bottomRight',
          duration: 2000,
        })
        await new Promise((resolve) => setTimeout(resolve, 2000))
        window.location.href = registerJumpUrl
        return
      } else {
        const content = resolveApiErrorMessage(response.data, t['login.msg.registerFailed'])
        setErrorMessage(content)
        showErrorNotice(t['login.msg.registerTitle'], content, 2000)
      }
    } catch (error: any) {
      const content = resolveApiErrorMessage(error, t['login.msg.registerFailed'])
      setErrorMessage(content)
      showErrorNotice(t['login.msg.registerTitle'], content, 2000)
    } finally {
      setLoading(false)
    }
  }

  function gotoMode(registerMode: boolean) {
    if (registerMode) {
      const searchParams = new URLSearchParams()
      if (registerType) {
        searchParams.set('type', registerType)
      }
      if (inviteCodeFromUrl) {
        searchParams.set('inviteCode', inviteCodeFromUrl)
      }
      const queryString = searchParams.toString()
      const query = queryString ? `?${queryString}` : ''
      history.push(`/register${query}`)
      return
    }
    history.push('/login')
  }

  function onSubmitClick() {
    if (isRegisterMode && !canSubmitRegister) {
      return
    }
    formRef.current?.validate().then((values) => {
      if (isRegisterMode) {
        register(values)
        return
      }
      login(values)
    })
  }

  return (
    <div
      className={`${styles['login-form-wrapper']} ${
        isRegisterMode ? styles['login-form-wrapper-register'] : ''
      }`}
    >
      <div className={styles['login-form-title']}>
        <Row>
          <Col flex="auto">
            {isRegisterMode ? t['login.form.registerTitle'] : t['login.form.title']}
          </Col>
          <Col flex="50px">
            <Select
              triggerElement={<IconButton icon={<IconLanguage />} />}
              options={[
                { label: t['login.lang.zh'], value: 'zh-CN' },
                { label: t['login.lang.en'], value: 'en-US' },
              ]}
              value={lang}
              triggerProps={{
                autoAlignPopupWidth: false,
                autoAlignPopupMinWidth: true,
                position: 'br',
              }}
              trigger="hover"
              onChange={(value) => {
                setLang(value)
              }}
            />
          </Col>
        </Row>
      </div>
      <div className={styles['login-form-sub-title']}>
        {t['login.form.subtitle']} - {t['login.systemName']}
      </div>
      <div className={styles['login-form-error-msg']}>{errorMessage}</div>
      <Form
        className={styles['login-form']}
        layout="vertical"
        ref={formRef}
        initialValues={{
          name: companyFromUrl || '',
          email: '',
          phoneNumber: '',
          password: '',
          inviteCode: inviteCodeFromUrl,
        }}
      >
        {isRegisterMode ? (
          <div className={styles['register-grid']}>
            <Form.Item
              field="name"
              label={t['login.form.companyName.label']}
              rules={[{ required: true, message: t['login.form.companyName.errMsg'] }]}
            >
              <Input
                prefix={<IconUser style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.companyName.placeholder']}
                disabled={isSubAccountRegister}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="inviteCode"
              label={t['login.form.inviteCode.label']}
              validateTrigger="onBlur"
              rules={[{ required: true, message: t['login.form.inviteCode.errMsg'] }]}
            >
              <Input
                placeholder={t['login.form.inviteCode.placeholder']}
                disabled={isSubAccountRegister}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="phoneNumber"
              label={t['login.form.phoneNumber.label']}
              rules={[
                { required: true, message: t['login.form.phoneNumber.errMsg'] },
                {
                  match: PHONE_REGEX,
                  message: t['login.form.phoneNumber.invalid'],
                },
              ]}
            >
              <Input
                prefix={<IconUser style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.phoneNumber.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="email"
              label={t['login.form.email.label']}
              rules={[
                { required: true, message: t['login.form.email.errMsg'] },
                {
                  match: EMAIL_REGEX,
                  message: t['login.form.email.invalid'],
                },
              ]}
            >
              <Input
                prefix={<IconUser style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.email.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
          </div>
        ) : (
          <>
            <Form.Item
              field="phoneNumber"
              label={t['login.form.loginAccount.label']}
              rules={[
                {
                  required: true,
                  message: t['login.form.loginAccount.errMsg'],
                },
                {
                  validator(value, callback) {
                    const validationError =
                      getLoginIdentifierValidationError(value)

                    if (validationError === 'empty') {
                      callback()
                      return
                    }

                    if (validationError === 'invalid') {
                      callback(t['login.form.loginAccount.invalid'])
                      return
                    }

                    callback()
                  },
                },
              ]}
            >
              <Input
                prefix={<IconUser style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.loginAccount.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="password"
              label={t['login.form.password.label']}
              rules={[{ required: true, message: t['login.form.password.errMsg'] }]}
            >
              <Input.Password
                prefix={<IconLock style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.password.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
          </>
        )}
        {!isRegisterMode && (
          <div className={styles['login-form-forget-password']}>
            {/* {t['login.form.forgetPassword']} */}
          </div>
        )}
        <Button
          type="primary"
          long
          onClick={onSubmitClick}
          loading={loading}
          disabled={isRegisterMode && !canSubmitRegister}
        >
          {isRegisterMode ? t['login.form.registerSubmit'] : t['login.form.login']}
          <IconArrowRight style={{ marginLeft: 8 }} />
        </Button>
        <div className={styles['login-form-switch-row']}>
          <span>
            {isRegisterMode
              ? t['login.form.switch.toLoginPrefix']
              : t['login.form.switch.toRegisterPrefix']}
          </span>
          <Button
            type="text"
            className={styles['login-form-switch-btn']}
            onClick={() => gotoMode(!isRegisterMode)}
          >
            {isRegisterMode
              ? t['login.form.switch.toLoginAction']
              : t['login.form.switch.toRegisterAction']}
          </Button>
        </div>
      </Form>
    </div>
  )
}
